import { setContext } from '@apollo/client/link/context';
import { onError, ErrorResponse } from '@apollo/client/link/error';
import { fromPromise } from '@apollo/client';
import type { IStore } from '../types';
import { refreshTokens, logout } from './auth';
import { isAuthError } from './errors';
import { isSessionlessOperation } from './sessionlessOperations';
import type { StoreDispatch } from './types';

const authErrorsConcernCredentialsNotSession = (operationName?: string) =>
  isSessionlessOperation(operationName) || operationName === 'RefreshLegacyToken';

export const createAuthLink = (getStore: () => IStore, apiKey?: string) => 
  setContext(({ operationName }, { headers }) => ({
    headers: {
      ...headers,
      ...(getStore().token &&
        !isSessionlessOperation(operationName) && { authorization: `Bearer ${getStore().token}` }),
      ...(apiKey && { 'X-API-KEY': apiKey }),
    },
  }));

export const createErrorLink = (
  getStore: () => IStore,
  dispatch: StoreDispatch,
  apiUrl: string,
  apiKey?: string
) => {
  const refreshState = { current: null as Promise<string> | null };
  
  return onError(({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
    if (authErrorsConcernCredentialsNotSession(operation.operationName)) {
      return;
    }

    if (!isAuthError(graphQLErrors, networkError)) {
      return;
    }

    const refreshToken = getStore().refreshToken;
    if (!refreshToken || typeof refreshToken !== 'string') {
      logout(dispatch);
      return;
    }

    if (!refreshState.current) {
      refreshState.current = refreshTokens(apiUrl, refreshToken, dispatch, apiKey)
        .catch((error) => {
          logout(dispatch);
          throw error;
        })
        .finally(() => {
          refreshState.current = null;
        });
    }

    return fromPromise(refreshState.current).flatMap((token) => {
      operation.setContext(({ headers = {} }) => ({
        headers: { ...headers, authorization: `Bearer ${token}` },
      }));
      return forward(operation);
    });
  });
};
